import { ApiResponse } from "../../../../core/_models";
import { requestLaravel } from "../../../../../../utils/api";
import { pick } from "lodash";
import { getProjectFieldSection, saveProjectFieldSection, withProjectFieldValues } from "../../../core/project-fields/_request";
import { createSectionItem, deleteSectionItem, listSectionItems, ProjectSectionItem, SectionItemInput, SectionItemScope, sectionItemKeyValue, updateSectionItem } from "../../../core/section-items/_request";
import type { StepTab } from "../../ProjectInitiation-steps/core/_models";
import { IProjectScopeResourcesNeed, IFinancialAndFundingStrategyEngine, IGovernancePolicy, IPartnerAgency, IProcurementStrategy, IBaseCaseAndCounterfactualScenarioEngine, IDemandAndCapacityValidation, IFeasibilityOption, IFiscalSustainabilityEngine, ICbaCeaIntelligenceEngine, IRiskAndSensitivityAnalysisEngine, ISummaryMatrix, IDecisionData, IEconomiesOfScaleAssessment, ISpecialProcurementActionsAndSocialImpact, IProjectFeasibilityAndOptionsAnalysis } from "./_models";

export function saveProjectStageTwo(payload: { project_id: number; dk_stage: string }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<void>>(
        "/project-stage-two/stage/submit",
        { project_id: payload.project_id },
        { signal }
    );
}

export function storeOrUpdateOptionsIdentification(payload: IFeasibilityOption & { project_id: number }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IFeasibilityOption>>(
        "/project-stage-two/options-identification/save",
        payload,
        { signal }
    );
}

export function getOptionsIdentification(payload: { project_id: number }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IFeasibilityOption[]>>(
        "/project-stage-two/options-identification/list",
        payload,
        { signal }
    );
}

export function deleteOptionsIdentification(payload: { project_id: number; row_id: number }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<{ row_id: number }>>(
        "/project-stage-two/options-identification/delete",
        payload,
        { signal }
    );
}

export function getCbaCeaIntelligenceEngine(payload: { project_id: number }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<ICbaCeaIntelligenceEngine>>(
        "/project-stage-two/cba-cea-intelligence/get",
        payload,
        { signal }
    );
}

export function getSensitivityAnalysis(payload: { project_id: number }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IRiskAndSensitivityAnalysisEngine & { summary_matrix: ISummaryMatrix[] }>>(
        "/project-stage-two/sensitivity-analysis/get",
        payload,
        { signal }
    );
}

export function getStrategicResourcingDecisionEngine(payload: { project_id: number }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IDecisionData | null>>(
        "/project-stage-two/strategic-resourcing-decision/get",
        payload,
        { signal }
    );
}



export function getJustificationForOptions(payload: { project_id: number }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IProjectFeasibilityAndOptionsAnalysis>>(
        "/project-stage-two/justification-of-option/get",
        payload,
        { signal }
    );
}

/* =========================
   PROJECT-FIELDS SECTIONS
========================= */

const STRATEGIC_RESOURCING_STAGE_ID = 2;
// The stage key that section items and documents are saved against.
export const STAGE_TWO_KEY = "project_stage_2";
const PROJECT_SCOPE_RESOURCES_NEED_SECTION = "project_scope_resources_need";
// Each Project Scope block's evidence documents are stored against its own section.
export const PROJECT_SCOPE_DOCUMENT_SECTIONS = {
    supplementary_infrastructure: "project_scope_supplementary_infrastructure",
    boundaries: "project_scope_boundaries",
    land_property: "project_scope_land_property",
    professional_services: "project_scope_professional_services",
    technology_equipment: "project_scope_technology_equipment",
} as const;
// Also the section the step's supporting documents are stored against.
export const PROCUREMENT_STRATEGY_SECTION = "procurement_strategy";
const GOVERNANCE_POLICY_SECTION = "governance_policy_enabling_environment";
// Signed agreements are stage 2 documents stored against the Institutional Arrangements sub-section.
export const SIGNED_AGREEMENTS_SECTION = "institutional_arrangements";
// Additional supporting documents are stage 2 documents stored against the Policy & Compliance sub-section.
export const POLICY_COMPLIANCE_DOCUMENTS_SECTION = "policy_compliance";
const FINANCIAL_AND_FUNDING_STRATEGY_SECTION = "financial_and_funding_strategy_engine";
// The step key stays fiscal_sustainability_engine_ifsa; its project-fields section is named ..._ifisa.
const DEMAND_CAPACITY_SECTION = "demand_and_capacity_validation";
const BASE_CASE_SECTION = "base_case_and_counterfactual_scenario_engine";
const FISCAL_SUSTAINABILITY_SECTION = "fiscal_sustainability_engine_ifisa";
const SPECIAL_PROCUREMENT_SECTION = "special_procurement_actions_and_social_impact";
const ECONOMIES_OF_SCALE_SECTION = "economies_of_scale_engine";

// The fields each section saves; a field the section does not have is rejected.
const PROJECT_SCOPE_RESOURCES_NEED_FIELDS: (keyof IProjectScopeResourcesNeed)[] = ["scope_supplementary_infrastructure_narrative", "scope_supplementary_infrastructure_evidence_reference", "scope_boundaries_narrative", "scope_in_scope", "scope_out_of_scope", "scope_boundaries_evidence_reference", "scope_land_property_is_land_identified", "scope_land_property_is_land_ownership_confirmed", "scope_land_property_is_zoning_compliant", "scope_land_property_narrative", "scope_land_property_evidence_reference", "scope_professional_services_narrative", "scope_professional_services_evidence_reference", "scope_technology_equipment_narrative", "scope_technology_equipment_evidence_reference"];
const BASE_CASE_FIELDS: (keyof IBaseCaseAndCounterfactualScenarioEngine)[] = ["counterfactual_scenario_type", "current_capacity", "base_case_current_demand", "current_service_gap", "current_cost_of_service", "consequences_of_no_intervention", "base_case_confidence_level"];
const FISCAL_SUSTAINABILITY_FIELDS: (keyof IFiscalSustainabilityEngine)[] = ["operating_cost_coverage", "lifecycle_cost_coverage", "revenue_sufficiency", "fiscal_affordability_index", "funding_gap_percentage", "debt_exposure", "contingent_liability_exposure"];
const SPECIAL_PROCUREMENT_FIELDS: (keyof ISpecialProcurementActionsAndSocialImpact)[] = ["gender_responsive_procurement", "social_benefits_value_reference", "sustainability_and_environmental_compliance", "legal_and_technical_due_diligence"];
const ECONOMIES_OF_SCALE_FIELDS: (keyof IEconomiesOfScaleAssessment)[] = ["economies_of_scale_assessment"];

function readStageSection<T>(projectId: string | number, sectionKey: string, signal?: AbortSignal) {
    return getProjectFieldSection(projectId, STRATEGIC_RESOURCING_STAGE_ID, sectionKey, signal)
        .then((response) => withProjectFieldValues<T>(response));
}

function writeStageSection<T>(projectId: string | number, sectionKey: string, payload: object, signal?: AbortSignal) {
    return saveProjectFieldSection(projectId, STRATEGIC_RESOURCING_STAGE_ID, sectionKey, payload, signal)
        .then((response) => withProjectFieldValues<T>(response));
}

export function getProjectScopeResourcesNeed(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<IProjectScopeResourcesNeed>(project_id, PROJECT_SCOPE_RESOURCES_NEED_SECTION, signal);
}

export function updateProjectScopeResourcesNeed(project_id: number | string, values: IProjectScopeResourcesNeed, signal?: AbortSignal) {
    return writeStageSection<IProjectScopeResourcesNeed>(project_id, PROJECT_SCOPE_RESOURCES_NEED_SECTION, pick(values, PROJECT_SCOPE_RESOURCES_NEED_FIELDS), signal);
}

// Reads the parent section, which returns every tab's sub-section fields in one call.
export function getProcurementStrategy(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<IProcurementStrategy>(project_id, PROCUREMENT_STRATEGY_SECTION, signal);
}

export function getGovernancePolicyEnablingEnvironment(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<IGovernancePolicy>(project_id, GOVERNANCE_POLICY_SECTION, signal);
}

// Partner agencies are stage 2 section items: the agency name is the title, the deliverables
// the description, and the role the key_value of the item's "role" key.
const partnerAgencyScope = (project_id: number | string): SectionItemScope => ({
    project_id: Number(project_id),
    stage_id: STAGE_TWO_KEY,
    section: "partner_agency",
});

const toPartnerAgency = (item: ProjectSectionItem): IPartnerAgency => ({
    partner_agency_id: item.id,
    agency_name: item.title,
    role: sectionItemKeyValue(item, "role"),
    deliverables: item.description ?? "",
});

const toPartnerAgencyInput = (agency: IPartnerAgency): SectionItemInput => ({
    title: agency.agency_name,
    description: agency.deliverables || null,
    keys: { role: agency.role || null },
});

export async function getPartnerAgencies(project_id: number | string, signal?: AbortSignal): Promise<ApiResponse<IPartnerAgency[]>> {
    const response = await listSectionItems(partnerAgencyScope(project_id), signal);
    return { ...response, data: (response.data ?? []).map(toPartnerAgency) };
}

// Creates the agency when partner_agency_id is 0, otherwise updates it.
export async function savePartnerAgency(project_id: number | string, agency: IPartnerAgency): Promise<ApiResponse<IPartnerAgency>> {
    const scope = partnerAgencyScope(project_id);
    const input = toPartnerAgencyInput(agency);
    const response = agency.partner_agency_id
        ? await updateSectionItem(scope, agency.partner_agency_id, input)
        : await createSectionItem(scope, input);
    return { ...response, data: response.data ? toPartnerAgency(response.data) : response.data };
}

export function deletePartnerAgency(project_id: number | string, partner_agency_id: number) {
    return deleteSectionItem(partnerAgencyScope(project_id), partner_agency_id);
}

export function getFinancialAndFundingStrategy(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<IFinancialAndFundingStrategyEngine>(project_id, FINANCIAL_AND_FUNDING_STRATEGY_SECTION, signal);
}

export function getBaseCaseAndCounterfactualScenario(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<IBaseCaseAndCounterfactualScenarioEngine>(project_id, BASE_CASE_SECTION, signal);
}

export function updateBaseCaseAndCounterfactualScenario(project_id: number | string, values: IBaseCaseAndCounterfactualScenarioEngine, signal?: AbortSignal) {
    return writeStageSection<IBaseCaseAndCounterfactualScenarioEngine>(project_id, BASE_CASE_SECTION, pick(values, BASE_CASE_FIELDS), signal);
}

// Reads the parent section, which returns both tabs' sub-section fields in one call.
export function getDemandAndCapacityValidation(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<IDemandAndCapacityValidation>(project_id, DEMAND_CAPACITY_SECTION, signal);
}

export function getFiscalSustainability(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<IFiscalSustainabilityEngine>(project_id, FISCAL_SUSTAINABILITY_SECTION, signal);
}

export function updateFiscalSustainability(project_id: number | string, values: IFiscalSustainabilityEngine, signal?: AbortSignal) {
    return writeStageSection<IFiscalSustainabilityEngine>(project_id, FISCAL_SUSTAINABILITY_SECTION, pick(values, FISCAL_SUSTAINABILITY_FIELDS), signal);
}

export function getSpecialProcurementActionsAndSocialImpact(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<ISpecialProcurementActionsAndSocialImpact>(project_id, SPECIAL_PROCUREMENT_SECTION, signal);
}

export function updateSpecialProcurementActionsAndSocialImpact(project_id: number | string, values: ISpecialProcurementActionsAndSocialImpact, signal?: AbortSignal) {
    return writeStageSection<ISpecialProcurementActionsAndSocialImpact>(project_id, SPECIAL_PROCUREMENT_SECTION, pick(values, SPECIAL_PROCUREMENT_FIELDS), signal);
}

export function getEconomiesOfScaleAssessment(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<IEconomiesOfScaleAssessment>(project_id, ECONOMIES_OF_SCALE_SECTION, signal);
}

export function updateEconomiesOfScaleAssessment(project_id: number | string, values: IEconomiesOfScaleAssessment, signal?: AbortSignal) {
    return writeStageSection<IEconomiesOfScaleAssessment>(project_id, ECONOMIES_OF_SCALE_SECTION, pick(values, ECONOMIES_OF_SCALE_FIELDS), signal);
}

// Saves one tab of a tab-wise step through its project-fields sub-section, sending only the
// tab's fields; the response holds only that tab's fields.
export function saveStepTab(project_id: number, tab: StepTab & { section: string }, values: object) {
    return writeStageSection<Record<string, unknown>>(project_id, tab.section, pick(values, tab.fields));
}

/* =========================
   PROCUREMENT STRATEGY: BFI ALTERNATIVE FINANCING MODELS
========================= */
// Reference rows (not project data) for the Procurement Strategy Type table.
export interface IBfiAlternativeFinancingModel {
    id: number;
    [key: string]: string | number;
}

export function getBfiAlternativeFinancingModel(signal?: AbortSignal) {
    return requestLaravel<ApiResponse<{ list: IBfiAlternativeFinancingModel[] }>>(
        "/project-stage-three/bfi-alternative-financing-models/list",
        {},
        { signal }
    );
}
